import { CENTRA_LANGUAGE_CODE, type CentraLanguageCode } from "@/data/supported-languages"
import { isContentVisible } from "@/src/utils/content/isContentVisible"
import { type ResolvedBadge, type SanityInjectBadge } from "@/src/utils/badges/types"

/**
 * Parses the raw sanity_inject_badge JSON string into typed badge entries.
 * Returns an empty array on invalid or missing input.
 */
function parseSanityInjectBadges(json: string | null | undefined): SanityInjectBadge[] {
    if (!json) return []

    try {
        const parsed: unknown = JSON.parse(json)

        if (!Array.isArray(parsed)) return []

        return parsed as SanityInjectBadge[]
    } catch {
        return []
    }
}

/**
 * Resolves localized text from a Sanity localeString object.
 * Falls back to English, then to the first non-empty translation.
 * The `_type` marker is never a translation and is skipped.
 */
function resolveLocalizedText(
    text: Record<string, string> | undefined,
    language: CentraLanguageCode
): string | undefined {
    if (!text) return undefined

    const firstTranslation = Object.entries(text).find(
        ([key, value]) => key !== "_type" && typeof value === "string" && value
    )?.[1]

    return text[language] || text[CENTRA_LANGUAGE_CODE.En] || firstTranslation
}

/**
 * Parses the sanity_inject_badge JSON string and returns badges
 * resolved for the given language and filtered by the unified visibility
 * control (market + locale).
 *
 * @param json - Raw JSON string from sanity_inject_badge / sanity_inject_badge_json
 * @param language - Current language code (e.g. "en", "de")
 * @param locale - Current app locale (e.g. "en-se", "en")
 */
export function resolveSanityInjectBadges(
    json: string | null | undefined,
    language: CentraLanguageCode,
    locale: string
): ResolvedBadge[] {
    const badges = parseSanityInjectBadges(json)

    return badges
        .filter(badge => isContentVisible(badge, locale))
        .map(badge => ({
            type: badge.type,
            text: resolveLocalizedText(badge.text, language),
            textColor: badge.textColor,
            backgroundColor: badge.backgroundColor?.hex,
            image: badge.image?.asset?.url
        }))
        .filter(badge => badge.text || badge.image)
}
